<template>
  <header class="appbar">
    <div class="appbar-inner">
      <router-link to="/" class="appbar-title">{{ title }}</router-link>
      <button class="theme-appbar-button" @click="toggleModal" :style="{ marginLeft: 'auto' }">
        <span class="pi pi-info-circle" :style="{ color: 'var(--color-on-primary)', fontSize: '1.0rem' }"></span>
      </button>
      <button class="theme-appbar-button" @click="toggleTheme">
        <span class="pi" :class="isDark ? 'pi-moon' : 'pi-sun'"
          :style="{ color: 'var(--color-on-primary)', fontSize: '1.0rem' }"></span>
      </button>
      <LanguageDropdown />
    </div>
  </header>
  <div class="router-view-wrapper"><router-view /></div>

  <Transition name="modal">
    <div v-if="isOpenModal" class="modal-backdrop" @click.self="toggleModal">
      <div class="modal">
        <div class="modal-header">
          <span class="modal-title">{{ $t('modal.title') }}</span>
          <button class="modal-close" @click="toggleModal">
            <span class="pi pi-times"></span>
          </button>
        </div>
        <div class="modal-body">
          <p>{{ $t('description') }}</p>
          <hr />
          <h5>{{ $t('modal.copyrightTitle') }}</h5>
          <p>{{ $t('modal.copyrightBody1') }}</p>
          <p>{{ $t('modal.copyrightBody2') }}</p>
          <hr />
          <h5>{{ $t('modal.bugsTitle') }}</h5>
          <i18n-t keypath="modal.bugsBody1" tag="p">
            <template #formLink>
              <a href="https://forms.gle/2eTvhCXFKBBrt3sMA" target="_blank">{{ $t('modal.formLinkText') }}</a>
            </template>
          </i18n-t>
          <p>{{ $t('modal.bugsBody2') }}</p>
          <hr />
          <h5>{{ $t('modal.developer') }}</h5>
          <p> <a href="https://twitter.com/108_chocolat" target="_blank"><img height="12" width="12" :src="isDark ? 'https://cdn.simpleicons.org/x/ffffff' : 'https://cdn.simpleicons.org/x/000000'" style="margin-right: 4px;" />108_chocolat</a></p>
          <hr />
          <h5>{{ $t('modal.licenseTitle') }}</h5>
          <p>flag-icons - Copyright &copy; Panayiotis Lipiridis (MIT License)</p>
          <p>PrimeIcons - Copyright &copy; PrimeTek (MIT License)</p>
          <hr />
          <div class="dev-info">
            <p>{{ $t('modal.songsUpdated') }}: {{ new Date(config.updatedAt).toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) }}</p>
            <p>{{ $t('modal.appVersion') }}: {{ appVersion }}<template v-if="commitDate"> ({{ commitDate }})</template></p>
            <p>Copyright &copy; {{ new Date().getFullYear() }} <a href="https://twitter.com/108_chocolat"
                target="_blank">108_chocolat</a></p>
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <footer class="footer">
    <div class="footer-inner">
      <span>{{ $t('footer') }}</span>
    </div>
  </footer>
</template>

<script>
import config from './assets/data/songs.js'
import LanguageDropdown from './components/LanguageDropdown.vue'
import 'primeicons/primeicons.css'

export default {
  name: 'App',
  components: {
    LanguageDropdown
  },
  data() {
    return {
      appVersion: "v0.72",
      commitDate: __COMMIT_DATE__,
      config: config,
      isDark: false,
      isOpenModal: false
    }
  },
  mounted() {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    this.isDark = mq.matches
    document.documentElement.setAttribute('data-theme', this.isDark ? 'dark' : 'light')
    this.mediaQueryRef = mq
    mq.addEventListener('change', this.onSystemThemeChange)
  },
  beforeUnmount() {
    if (this.mediaQueryRef) {
      this.mediaQueryRef.removeEventListener('change', this.onSystemThemeChange)
    }
  },
  computed: {
    title() {
      const localeMap = { ja_JP: 'ja', en_US: 'en', kr_KR: 'kr' }
      const key = localeMap[this.$i18n.locale] || 'ja'
      document.title = config.siteTitle[key]
      return config.siteTitle[key]
    }
  },
  methods: {
    onSystemThemeChange(e) {
      this.isDark = e.matches
      document.documentElement.setAttribute('data-theme', this.isDark ? 'dark' : 'light')
    },
    toggleTheme() {
      this.isDark = !this.isDark
      document.documentElement.setAttribute('data-theme', this.isDark ? 'dark' : 'light')
    },
    toggleModal() {
      this.isOpenModal = !this.isOpenModal
      if (this.isOpenModal) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
        document.body.style.overflow = 'hidden'
        document.body.style.paddingRight = `${scrollbarWidth}px`
      } else {
        document.body.style.overflow = ''
        document.body.style.paddingRight = ''
      }
    }
  },
}
</script>

<style>
:root,
:root[data-theme='light'] {
  --home-bg-gradient: linear-gradient(160deg, #fdf8ff 0%, #f7ecff 100%);
  --color-primary: #9f58bb;
  --color-secondary: #f3e8ff;
  --color-on-primary: #ffffff;
  --color-on-secondary: #000000;
  --color-highlight: #e0d7ff;
  --color-accent: #9b009b;
  --color-shadow: rgba(0, 0, 0, 0.3);
  --color-shadow-light: rgba(0, 0, 0, 0.2);
  --color-bg: #ffffff;
  --color-text-primary: #242424;
  --color-text-secondary: #757575;
  --color-text-highlight: #9f58bb;
  --color-surface: #f0e3ff;
}

:root[data-theme='dark'] {
  --home-bg-gradient: linear-gradient(160deg, #121212 0%, #2a1640 100%);
  --color-primary: #481c5a;
  --color-highlight: rgb(77, 53, 92);
  --color-accent: #feff6b;
  --color-shadow: rgba(0, 0, 0, 0.5);
  --color-shadow-light: rgba(0, 0, 0, 0.4);
  --color-bg: #12021f;
  --color-text-primary: #e0e0e0;
  --color-text-secondary: #b0b0b0;
  --color-text-highlight: #c48bdb;
  --color-surface: #33253f;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: 'Noto Sans JP', 'Noto Sans KR', 'Noto Sans', sans-serif;
  background-color: var(--color-bg);
  color: var(--color-text-primary);
  transition: background-color 0.2s, color 0.2s;
}

#app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.router-view-wrapper {
  flex: 1;
}

.appbar {
  background-color: var(--color-primary);
  color: var(--color-on-primary);
  padding: 0 24px;
  box-shadow: 0 2px 4px var(--color-shadow);
  position: sticky;
  top: 0;
  z-index: 100;
}

.appbar-inner {
  display: flex;
  align-items: center;
  height: 56px;
  /* max-width: 1200px; */
  margin: 0 auto;
}

.appbar-title {
  font-size: 1.25rem;
  font-weight: bold;
  letter-spacing: 0.05em;
  color: var(--color-on-primary);
  text-decoration: none;
  cursor: pointer;
}

.appbar-nav {
  display: flex;
  gap: 16px;
}

.theme-appbar-button {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  margin-right: 8px;
  padding: 4px 6px;
  border-radius: 4px;
  line-height: 1;
  transition: background-color 0.15s;
}

.theme-appbar-button:hover {
  background-color: rgba(255, 255, 255, 0.15);
}

.footer {
  background-color: var(--color-primary);
  color: var(--color-on-primary);
  padding: 0 24px;
  box-shadow: 0 -2px 4px var(--color-shadow-light);
  /* position: sticky; */
  bottom: 0;
  z-index: 100;
}

.footer-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  font-size: 0.85rem;
  opacity: 0.85;
  margin: 2px auto;
}

.footer a {
  color: var(--color-on-primary);
  text-decoration: underline;
}

@media (max-width: 600px) {
  .appbar {
    padding: 0 12px;
  }

  .footer {
    padding: 0 12px;
  }

  .footer-inner {
    font-size: 0.7rem;
    height: auto;
    text-align: center;
    padding: 4px 0;
  }
}

/* Modal */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 16px;
}

.modal {
  background-color: var(--color-bg);
  color: var(--color-text-primary);
  border-radius: 8px;
  box-shadow: 0 8px 32px var(--color-shadow);
  width: 100%;
  max-width: 800px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  /* background-color: var(--color-primary); */
  /* color: var(--color-on-primary); */
  flex-shrink: 0;
}

.modal-title {
  font-size: 1.1rem;
  font-weight: bold;
  flex: 1;
}

.modal-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  line-height: 1;
  color: var(--color-accent);
  font-size: 1rem;
  transition: background-color 0.15s;
}

.modal-close:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  font-size: 0.9rem;
  min-height: 120px;
}

.modal-body h5 {
  font-size: 0.9rem;
  margin-bottom: 4px;
}

.modal-body p {
  margin-bottom: 0.25em;
  word-wrap: break-word;
}

.modal-body hr {
  border: none;
  border-top: 1px solid var(--color-highlight);
  margin: 16px 0;
}

.modal-body .dev-info {
  font-size: 0.8rem;
}

.modal-body a {
  color: var(--color-accent);
  text-decoration: none;
}

/* Modal transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .modal,
.modal-leave-active .modal {
  transition: transform 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal,
.modal-leave-to .modal {
  transform: scale(0.95);
}
</style>
